import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import {
    Box, Button, Typography, Stack, Divider, useTheme, Container, TextField, InputAdornment,
    IconButton, Tooltip, CircularProgress, Breadcrumbs, Link, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { useSelector, useDispatch } from 'react-redux';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import HomeIcon from '@mui/icons-material/Home';
import {
    selectProperties,
    selectGlobalProperty,
    selectLocalProperty,
    setLocalProperty,
    fetchProperties
} from '../../redux/propertySlice';
import ExcelJS from 'exceljs';

ModuleRegistry.registerModules([AllCommunityModule]);

const formatINR = (value) => {
    const num = Number(value) || 0;
    return `₹${num.toLocaleString('en-IN')}`;
};

// Function to extract month keys from data
const extractMonthKeys = (data) => {
    if (!data || !data.length) return [];
    const allMonths = new Set();
    data.forEach(item => {
        Object.keys(item).forEach(key => {
            if (key.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}$/)) {
                allMonths.add(key);
            }
        });
    });

    // Sort months chronologically
    return Array.from(allMonths).sort((a, b) => {
        const [monthA, yearA] = a.split('-');
        const [monthB, yearB] = b.split('-');
        const dateA = new Date(`20${yearA} ${monthA} 1`);
        const dateB = new Date(`20${yearB} ${monthB} 1`);
        return dateA - dateB;
    });
};

const CashFlowReport = () => {
    const theme = useTheme();
    const [fromDate, setFromDate] = useState(dayjs('2025-04-01'));
    const [toDate, setToDate] = useState(dayjs());
    const [cashFlowData, setCashFlowData] = useState([]);
    const [monthColumns, setMonthColumns] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const gridRef = useRef();
    const dispatch = useDispatch();
    const properties = useSelector(selectProperties);
    const printRef = useRef();
    const globalProperty = useSelector(selectGlobalProperty);
    const navigate = useNavigate();
    const localProperty = useSelector(state => selectLocalProperty(state, 'cashflowTable'));
    const selectedProperty = localProperty || globalProperty;
    const [SelectedProperty, setSelectedProperty] = useState(selectedProperty);

    useEffect(() => {
        setSelectedProperty(selectedProperty);
    }, [selectedProperty]);

    const handlePropertyChange = (event) => {
        const selectedId = event.target.value;
        setSelectedProperty(selectedId);
        dispatch(setLocalProperty({
            componentId: 'cashflowTable',
            propertyId: selectedId
        }));
    };

    // ─── Column Definitions ───────────────────────────────────────────────────
    const colDefs = useMemo(() => [
        {
            field: 'RowNum',
            headerName: 'S.No',
            width: 65,
            pinned: 'left',
            cellStyle: { textAlign: 'center' },
            lockPosition: true,
        },
        // {
        //     field: 'BookNo',
        //     headerName: 'Book No',
        //     width: 110,
        //     pinned: 'left',
        //     filter: 'agTextColumnFilter',
        //     lockPosition: true,
        // },
        {
            field: 'ResidentName',
            headerName: 'Resident Name',
            width: 180,
            pinned: 'left',
            filter: 'agTextColumnFilter',
            lockPosition: true,
        },
        {
            field: 'MobileNo',
            headerName: 'Mobile No',
            width: 120,
            pinned: 'left',
            filter: 'agTextColumnFilter',
            lockPosition: true,
        },
        {
            field: 'PlanName',
            headerName: 'Plan Name',
            width: 180,
            pinned: 'left',
            filter: 'agTextColumnFilter',
            lockPosition: true,
        },

        ...monthColumns.map((month) => ({
            field: month,
            headerName: month,
            width: 110,
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
            valueFormatter: (params) => formatINR(params.value),

        })),
        {
            field: 'TotalAmount',
            headerName: 'Total Amount',
            width: 130,
            pinned: 'right',
            cellStyle: {
                textAlign: 'right',
                fontWeight: 700,
                color: '#4caf50',
                backgroundColor: '#e8f5e9',
            },
            valueFormatter: (params) => formatINR(params.value),
            lockPosition: true,
        },
    ], [monthColumns]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        tooltipValueGetter: (params) => params.value ?? '',
    }), []);


    const pinnedBottomRowData = useMemo(() => {
        if (!cashFlowData.length) return [];
        const totalRow = {
            RowNum: '',
            BookNo: 'TOTAL',
            ResidentName: `Records: ${cashFlowData.length}`,
            PlanName: '',
            MobileNo: '',
            TotalAmount: cashFlowData.reduce((s, r) => s + (Number(r.TotalAmount) || 0), 0),
        };
        monthColumns.forEach((month) => {
            totalRow[month] = cashFlowData.reduce((s, r) => s + (Number(r[month]) || 0), 0);
        });
        return [totalRow];
    }, [cashFlowData, monthColumns]);

    const getRowStyle = useCallback((params) => {
        if (params.node.rowPinned) {
            return { backgroundColor: '#e8f5e9', fontWeight: 'bold', color: '#1b5e20' };
        }
        return {
            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f9f9f9' : '#ffffff',
        };
    }, []);

    const fetchCashFlowData = async () => {
        if (!SelectedProperty) {
            toast.warning('Please select a property');
            return;
        }

        if (!fromDate || !toDate) {
            toast.warning('Please select date range');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                BookDtFrom: fromDate.format('YYYY-MM-DD'),
                BookUpToDt: toDate.format('YYYY-MM-DD'),
                PropId: SelectedProperty,
                Flag: ''
            };

            const response = await axiosInstance.post('TrnBooking/GetCashFlowMonth', payload);

            if (response.data.Status === 0) {
                const raw = response.data.Data.CashFlow || [];

                const months = extractMonthKeys(raw);
                setMonthColumns(months);

                const groupedData = new Map();

                raw.forEach((item) => {
                    const key = item.BookId;
                    if (!groupedData.has(key)) {
                        groupedData.set(key, {
                            BookId: item.BookId,
                            BookNo: item.BookNo,
                            PlanName: item.PlanName,
                            FirstName: item.FirstName,
                            MidName: item.MidName,
                            LastName: item.LastName,
                            PropId: item.PropId,
                            ResidentId: item.ResidentId,
                            MobileNo: item.MobileNo,
                            months: {},
                        });
                    }

                    const record = groupedData.get(key);

                    months.forEach(month => {
                        const value = Number(item[month]) || 0;
                        if (record.months[month]) {
                            record.months[month] += value;
                        } else {
                            record.months[month] = value;
                        }
                    });
                });

                const formatted = Array.from(groupedData.values()).map((item, idx) => {
                    const rowData = {
                        RowNum: idx + 1,
                        BookNo: item.BookNo,
                        ResidentName: `${item.FirstName || ''} ${item.MidName || ''} ${item.LastName || ''}`.trim(),
                        PlanName: item.PlanName,
                        MobileNo: item.MobileNo,
                        ...item.months,
                    };

                    rowData.TotalAmount = months.reduce((sum, month) => sum + (Number(rowData[month]) || 0), 0);

                    return rowData;
                });

                setCashFlowData(formatted);
                if (!formatted.length) toast.info('No cash flow data found for the selected date range.');
            } else {
                toast.info(response.data.Message || 'No data found.');
                setCashFlowData([]);
                setMonthColumns([]);
            }
        } catch (error) {
            toast.error('Error fetching cash flow data from the server.');
            setCashFlowData([]);
            setMonthColumns([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (SelectedProperty && fromDate && toDate) {
            fetchCashFlowData();
        }
    }, [SelectedProperty, fromDate, toDate]);

    const onSearchChange = useCallback((e) => {
        setSearchText(e.target.value);
        gridRef.current?.api?.setGridOption('quickFilterText', e.target.value);
    }, []);

    const PropertyFilterJSX = (
        <FormControl size="small" style={{ minWidth: '250px' }}>
            <InputLabel id="property-select-label">Select Property</InputLabel>
            <Select
                labelId="property-select-label"
                id="property-select"
                value={SelectedProperty || ''}
                onChange={handlePropertyChange}
                label="Select Property"
            >
                {properties.map((property) => (
                    <MenuItem key={property.Id} value={property.Id}>
                        {property.Name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `CashFlow_Report_${fromDate?.format('DD-MM-YYYY')}_to_${toDate?.format('DD-MM-YYYY')}`,
        pageStyle: `
            @page { size: A4 landscape; margin: 0.8cm; }
            @media print {
                body { font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .no-print { display: none !important; }
                .ag-theme-alpine { overflow: visible !important; }
                .ag-root-wrapper { overflow: visible !important; }
                .ag-body-viewport { overflow: visible !important; }
            }
        `,
    });

    const handleExportToExcel = async () => {
        if (!cashFlowData.length) {
            toast.warning('No data to export');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(`CashFlow_${fromDate?.format('YYYY')}`);

        // ==================== Light Blue Border ====================
        const lightBlueBorder = {
            top: { style: 'thin', color: { argb: 'FF81C4FF' } },
            left: { style: 'thin', color: { argb: 'FF81C4FF' } },
            bottom: { style: 'thin', color: { argb: 'FF81C4FF' } },
            right: { style: 'thin', color: { argb: 'FF81C4FF' } }
        };

        // ==================== COLUMN DEFINITION ====================
        const columns = [
            { header: 'S.No', key: 'SNo', width: 8 },
            { header: 'Book No', key: 'BookNo', width: 12 },
            { header: 'Resident Name', key: 'ResidentName', width: 25 },
            { header: 'Plan Name', key: 'PlanName', width: 20 },
            { header: 'Mobile No', key: 'MobileNo', width: 15 },
            ...monthColumns.map(month => ({
                header: month,
                key: month,
                width: 14
            })),
            { header: 'Total Amount', key: 'TotalAmount', width: 16 },
        ];

        worksheet.columns = columns;

        // ==================== ADD DATA ROWS ====================
        cashFlowData.forEach((item, index) => {
            const row = worksheet.addRow({
                SNo: index + 1,
                BookNo: item.BookNo || '',
                ResidentName: item.ResidentName || '',
                PlanName: item.PlanName || '',
                MobileNo: item.MobileNo || '',
                ...monthColumns.reduce((acc, month) => {
                    acc[month] = Number(item[month]) || 0;
                    return acc;
                }, {}),
                TotalAmount: Number(item.TotalAmount) || 0
            });

            // Alternate row coloring + Light Blue Border
            row.eachCell((cell) => {
                if (index % 2 === 0) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                }
                cell.border = lightBlueBorder;
            });

            // Right align all numeric columns (month columns + Total Amount)
            const numericColumns = [...monthColumns, 'TotalAmount'];
            numericColumns.forEach(key => {
                const cell = row.getCell(key);
                if (cell.value !== null && cell.value !== undefined) {
                    cell.numFmt = '₹#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                }
            });
        });

        // ==================== ADD SUMMARY (TOTAL) ROW ====================
        const totalRow = worksheet.addRow({
            SNo: '',
            BookNo: 'TOTAL',
            ResidentName: `Records: ${cashFlowData.length}`,
            PlanName: '',
            MobileNo: '',
            ...monthColumns.reduce((acc, month) => {
                acc[month] = cashFlowData.reduce((sum, r) => sum + (Number(r[month]) || 0), 0);
                return acc;
            }, {}),
            TotalAmount: cashFlowData.reduce((sum, r) => sum + (Number(r.TotalAmount) || 0), 0)
        });

        // Style Total Row
        totalRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.border = lightBlueBorder;
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6E6E6' } }; // Light gray for total
        });

        // Right align numeric values in total row
        [...monthColumns, 'TotalAmount'].forEach(key => {
            const cell = totalRow.getCell(key);
            if (cell.value !== null && cell.value !== undefined) {
                cell.numFmt = '₹#,##0.00';
                cell.alignment = { horizontal: 'right' };
            }
        });

        // ==================== STYLE HEADER ROW ====================
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } }; // Same purple as your other report
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = lightBlueBorder;
        });

        headerRow.height = 26;

        // ==================== FREEZE HEADER ====================
        worksheet.views = [{ state: 'frozen', ySplit: 1 }];

        // ==================== EXPORT ====================
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fileName = `CashFlow_${fromDate?.format('YYYY') || 'Report'}.xlsx`;
        saveAs(blob, fileName);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ height: '90vh', backgroundColor: 'rgb(249, 249, 249)', position: 'relative' }}>
                {/* ── Top Bar ── */}
                <Box sx={{
                    backgroundColor: '#ffffff',
                    padding: { xs: '8px 16px', sm: '12px 16px' },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    borderRadius: '12px',
                    margin: '10px auto',
                    maxWidth: '95%',
                    flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                    gap: 2,
                }}>
                    <Breadcrumbs aria-label="breadcrumb" separator="›"
                        sx={{ fontSize: { xs: '14px', sm: '16px' }, display: 'flex', flexDirection: { xs: 'column', sm: 'column', md: 'row' } }}
                    >
                        <Link underline="hover" color="#635bff"
                            sx={{ cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center' }}
                            onClick={() => navigate('/dashboard')}
                        >
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            <Box component="span">Home</Box>
                        </Link>
                        <Link underline="hover" color="#635bff"
                            sx={{ cursor: 'pointer', fontWeight: '500' }}
                            onClick={() => navigate('/reports/accountsFinance')}
                        >
                            Account & Finance
                        </Link>
                        <Typography color="text.primary" sx={{ fontWeight: '500' }}>Cash Flow Report</Typography>
                    </Breadcrumbs>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                        sx={{ flexWrap: 'nowrap', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, width: 'auto' }}
                    >
                        {PropertyFilterJSX}

                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(newValue) => setFromDate(newValue)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: 150, width: 110, bgcolor: 'white', borderRadius: 2 }
                                }
                            }}
                        />

                        <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={(newValue) => setToDate(newValue)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: 150, width: 110, bgcolor: 'white', borderRadius: 2 }
                                }
                            }}
                        />

                        <Stack direction="row" spacing={1}>
                            {/* <Tooltip title="Refresh data" arrow>
                                <Button variant="contained" onClick={fetchCashFlowData}
                                    sx={{ height: 36, bgcolor: '#635bff', color: 'white', fontSize: '0.8rem', fontWeight: 'medium', borderRadius: 2, textTransform: 'none', px: 2, '&:hover': { bgcolor: '#4a42cc' } }}
                                >
                                    Refresh
                                </Button>
                            </Tooltip> */}
                            {/* <Tooltip title="Print the report" arrow>
                                <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}
                                    disabled={!cashFlowData.length}
                                    sx={{ height: 36, borderColor: 'secondary.main', color: 'secondary.main', fontSize: '0.8rem', fontWeight: 'medium', borderRadius: 2, textTransform: 'none', px: 2 }}
                                >
                                    Print
                                </Button>
                            </Tooltip> */}
                            <Tooltip title="Export to Excel" arrow>
                                <Button variant="outlined" startIcon={<ImportExportIcon />} onClick={handleExportToExcel}
                                    disabled={!cashFlowData.length}
                                    sx={{ height: 36, borderColor: 'success.main', color: 'success.main', fontSize: '0.8rem', fontWeight: 'medium', borderRadius: 2, textTransform: 'none', px: 2 }}
                                >
                                    Export
                                </Button>
                            </Tooltip>
                            <Tooltip title="Previous Page" arrow>
                                <IconButton
                                    sx={{ width: 36, height: 36, color: '#ffffff', bgcolor: '#635bff', ':hover': { bgcolor: '#4a42cc' } }}
                                    onClick={() => navigate('/reports/accountsFinance')}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Box>

                {/* ── Main Container ── */}
                <Container maxWidth={false}
                    sx={{ width: '95%', mt: 2, p: 3, pb: 1, bgcolor: 'grey.50', borderRadius: 4, boxShadow: theme.shadows[10], mx: 'auto' }}
                >
                    <ToastContainer />

                    {/* Search */}
                    <Box sx={{ mb: 2 }} className="no-print">
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by Resident Name, Book No, Plan Name, Mobile No..."
                            value={searchText}
                            onChange={onSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: 'white', borderRadius: 2, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.8 }, boxShadow: theme.shadows[1] },
                            }}
                        />
                    </Box>

                    <Divider sx={{ mb: 2, borderColor: 'grey.200', borderWidth: 1 }} />

                    {loading && (
                        <Box sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress size="3rem" sx={{ mb: 1 }} />
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>Loading data...</Typography>
                        </Box>
                    )}

                    {/* AG Grid with horizontal scroll only for months */}
                    <Box
                        className="ag-theme-alpine"
                        sx={{
                            height: 'calc(100vh - 260px)',
                            width: '100%',
                            '& .ag-root-wrapper': {
                                borderRadius: '8px',
                            }
                        }}
                        ref={printRef}
                    >
                        <AgGridReact
                            ref={gridRef}
                            rowData={cashFlowData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            pagination={true}
                            paginationPageSize={100}
                            paginationPageSizeSelector={[50, 100, 500, 1000]}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            rowHeight={28}
                            headerHeight={32}
                            floatingFiltersHeight={28}
                            enableCellTextSelection={true}
                            tooltipShowDelay={0}
                            overlayNoRowsTemplate={
                                loading
                                    ? '<span></span>'
                                    : '<span style="color:#888;font-style:italic">No cash flow data found for the selected date range.</span>'
                            }

                            suppressHorizontalScroll={false}
                            alwaysShowHorizontalScroll={true}
                        />
                    </Box>
                </Container>
            </Box>
        </LocalizationProvider>
    );
};

export default CashFlowReport;